import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { UserChip } from '@tecuity/granite-ui/UserChip'
import { DetailList } from '@tecuity/granite-ui/DetailList'
import { Spinner } from '../Loaders/Loaders'
import { SET_RECEIPT_WORKVIEW_ITEM } from '../Accounting/ReceiptValidation/ReceiptValidationReducer'
import CheckmarkIcon from '@tecuity/sos-icons/react/check'
import { setErrorToast, setSuccessToast } from '../../actions'
import * as api from '../../requests'
import { ADD_PDF_TO_LIST, pdfTypes } from '../ReportViewer/ReportViewerReducer'
import { checkDLNValid } from '../BOSearch/BOSearchActions'
import { openSpecificImage } from '../ImageViewer/ImageViewerActions'
import { FilingSourceTypeIds } from '../../Utilities/Utilities'
import { sendToSupervisor } from './WorkviewActions'
import { ConfirmDialog } from '../Modal/Modal'
import Portal from '../Portal/Portal'

const WebProcessModal = ({ navToCorrespondence, setWebModalOpen, refreshView }) => {
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const { selectedRow } = useSelector((state) => state.workview2, shallowEqual)
  const [loading, setLoading] = useState(false)
  const [loadingFields, setLoadingFields] = useState(false)
  const [fields, setFields] = useState([])
  const [showNoPaymentWarning, setShowNoPaymentWarning] = useState(false)
  const [loadingNoPaymentData, setLoadingNoPaymentData] = useState(false)
  const history = useHistory()
  const dispatch = useDispatch()
  // TRADEMARK-AMENDMENT ONLY. Opened solely by approveItem when a trademark amendment is approved.
  const [cropPrompt, setCropPrompt] = useState({ open: false, onYes: null, onNo: null, onDismiss: null })
  const cropActionTakenRef = useRef(false)

  const runNormalApproval = () => {
    //TODO: For updating OPR for all modules please update
    //This or check takes notary OPR's into account.
    if (selectedRow.EntryTypeName === 'Web Prepared' || selectedRow.SourceTypeId === FilingSourceTypeIds.Notary) {
      setLoading(true)
      api.GET_WEB_PREPARED_RECEIPT_BALANCED(
        selectedRow.ImageEnvelopeId,
        (response) => {
          if (response.isReceiptBalanced) {
            api.APPROVE_FILING_BY_WORKVIEW_ITEM_ID(
              selectedRow.WorkViewItemId,
              () => {
                setLoading(false)
                refreshView()
                dispatch(setSuccessToast(`Successfully approved ${selectedRow.ResourceName}`))
                setWebModalOpen(false)
              },
              (err) => {
                console.error(err)
                setLoading(false)
                dispatch(setErrorToast(err.Message))
              }
            )
          } else {
            if (selectedRow.SourceTypeId === FilingSourceTypeIds.Notary)
            {
              dispatch({ type: SET_RECEIPT_WORKVIEW_ITEM, workviewItem: selectedRow })
              appUserRights.includes(`ReceiptView`) &&
                      history.push(`/accounting/receipt/${response.receiptNum}`)
            }else{
              api.ADD_RECEIPT_ITEM(
                {
                  DLN: selectedRow.DLN,
                  ImageEnvelopeID: selectedRow.ImageEnvelopeId,
                  ImageGroupID: selectedRow.ImageGroupId,
                  SourceID: selectedRow.SourceId,
                  SourceTypeID: selectedRow.SourceTypeId,
                },
                (response) => {
                  if (response.receipt && response.receipt.ReceiptNum) {
                    dispatch({ type: SET_RECEIPT_WORKVIEW_ITEM, workviewItem: selectedRow })
                    dispatch(setSuccessToast(`Receipt item has been created.`))
                    appUserRights.includes(`ReceiptView`) &&
                      history.push(`/accounting/receipt/${response.receipt.ReceiptNum}`)
                  } else if (!response.receipt) {
                    api.APPROVE_FILING_BY_WORKVIEW_ITEM_ID(
                      selectedRow.WorkViewItemId,
                      () => {
                        setLoading(false)
                        refreshView()
                        if (selectedRow.SourceTypeId == FilingSourceTypeIds.Facsimile) {
                          // FAC SIG
                          dispatch({
                            type: ADD_PDF_TO_LIST,
                            pdfInfo: {
                              filename: `Facsimile-${selectedRow.Filing.Num}.pdf`,
                              type: pdfTypes.FACSIMILE,
                              facsimileNum: selectedRow.Filing.Num,
                            },
                          })
                        }
                        dispatch(setSuccessToast(`Successfully approved ${selectedRow.ResourceName}`))
                        setWebModalOpen(false)
                      },
                      (err) => {
                        console.error(err)
                        setLoading(false)
                        dispatch(setErrorToast(err.Message))
                      }
                    )
                    return
                  } else {
                    dispatch(
                      setErrorToast('Logical flow not valid. WorkItem NOT approved. Contact Admin.')
                    )
                    setLoading(false)
                  }
                },
                (err) => {
                  console.error(err)
                  setLoading(false)
                  dispatch(setErrorToast(err.Message))
                }
              )
            }
          }
        },
        (err) => {
          setLoading(false)
          setErrorToast(`Failed to validate receipt information`)
          console.error(err)
        }
      )
    } else {
      setLoading(true)
      api.APPROVE_FILING_BY_WORKVIEW_ITEM_ID(
        selectedRow.WorkViewItemId,
        () => {
          refreshView()
          dispatch(setSuccessToast(`Successfully approved ${selectedRow.ResourceName}`))
          if (selectedRow.SourceTypeId === FilingSourceTypeIds.ServiceOfProcess) {
            api.GET_SERVICE_OF_PROCESS_FILING_IS_APPROVED(
              selectedRow.Filing.Num,
              (isApproved) => {
                if (isApproved === true || isApproved === 'true') {
                  const pdfInfo = {
                    ReceiptNum: null,
                    type: pdfTypes.SERVICE_OF_PROCESS,
                    FilingNum: selectedRow.Filing.Num,
                    filename: `SOP-${selectedRow.Filing.Num}.pdf`,
                  }
                  dispatch({ type: ADD_PDF_TO_LIST, pdfInfo: { ...pdfInfo } })
                }
              },
              (err) => console.error(err)
            )
          } else if (process.env.OPEN_REPORT_VIEWER_ON_WEB_FILED_PROCESSING === true) {
            const pdfInfo = {
              ReceiptNum: null,
              type: null,
            }
            switch (selectedRow.SourceTypeId) {
              case FilingSourceTypeIds.Business:
                pdfInfo.AmendmentNum = selectedRow.Filing.Num
                pdfInfo.type = pdfTypes.BUSINESS_ENTITY
                pdfInfo.filename = `Business-${selectedRow.Filing.Num}.pdf`
                break
              case FilingSourceTypeIds.Trademark:
                pdfInfo.type = pdfTypes.TRADEMARK
                pdfInfo.filename = `Trademark-${selectedRow.Filing.Num}.pdf`
                pdfInfo.trademarkAmendmentNumber = selectedRow.Filing.Num
                break
              case FilingSourceTypeIds.Orders:
                pdfInfo.filename = `Order-${selectedRow.Filing.Num}.pdf`
                pdfInfo.type = pdfTypes.ORDER
                pdfInfo.orderNum = selectedRow.Filing.Num
                break
              case FilingSourceTypeIds.Lien:
                pdfInfo.filename = `UCC-${selectedRow.Filing.Num}.pdf`
                pdfInfo.type = pdfTypes.UCC1
                pdfInfo.uccAmendmentNumber = selectedRow.Filing.Num
                break
              case FilingSourceTypeIds.Notary:
                pdfInfo.filename = `Notary-${selectedRow.Filing.Num}.pdf`
                pdfInfo.type = pdfTypes.NOTARY
                pdfInfo.filingNum = selectedRow.Filing.Num
                break
              case FilingSourceTypeIds.Apostille:
                pdfInfo.filename = `Apostille-${selectedRow.Filing.Num}.pdf`
                pdfInfo.type = pdfTypes.APOSTILLE
                pdfInfo.apostilleNum = selectedRow.Filing.Num
                break
              default:
            }
            if (pdfInfo.type) {
              dispatch({ type: ADD_PDF_TO_LIST, pdfInfo: { ...pdfInfo } })
            }
          }
          setWebModalOpen(false)
        },
        (error) => {
          setLoading(false)
          setErrorToast(error)
        }
      )
    }
  }

  // TRADEMARK ONLY: route the filing to the crop tool. The crop tool's onSaveCroppedImage then
  // finishes approval (or opens the receipt) via the shared receipt-balance flow.
  const routeToCropTool = () => {
    setLoading(true)
    api.SET_TRADEMARK_TO_IMAGE_CROP(
      selectedRow.WorkViewItemId,
      () => {
        setLoading(false)
        setWebModalOpen(false)
        history.push('/office/imageCropTool')
      },
      (err) => {
        console.error(err)
        setLoading(false)
        dispatch(setErrorToast('Failed to set the trademark to Pending Image Crop.'))
      }
    )
  }

  const approveItem = () => {
    // TRADEMARK ONLY. Initial filings always crop; amendments are prompted first (mirrors the
    // ProcessContextBar wizard). Every other source type falls through to the unchanged flow.
    if (selectedRow.SourceTypeId === FilingSourceTypeIds.Trademark) {
      if (selectedRow.IsInitialFiling) {
        routeToCropTool()
        return
      }
      // Amendment: mark Pending Image Crop the moment we prompt, so a dismissal or crash leaves it
      // recoverable in the WVM, then ask whether this amendment needs cropping.
      cropActionTakenRef.current = false
      api.SET_TRADEMARK_TO_IMAGE_CROP(
        selectedRow.WorkViewItemId,
        () => {},
        (err) => console.error(err)
      )
      setCropPrompt({
        open: true,
        onYes: () => {
          cropActionTakenRef.current = true
          routeToCropTool()
        },
        onNo: () => {
          cropActionTakenRef.current = true
          // Not cropping: move it out of Pending Image Crop, then approve exactly as before.
          api.SET_TRADEMARK_TO_PENDING_PAYMENT(
            selectedRow.WorkViewItemId,
            () => runNormalApproval(),
            (err) => {
              console.error(err)
              runNormalApproval()
            }
          )
        },
        // Dismissed (backdrop/Escape/X): leave it in Pending Image Crop and return to the work view.
        onDismiss: () => {
          setWebModalOpen(false)
          refreshView()
        },
      })
      return
    }
    runNormalApproval()
  }

  useEffect(() => {
    if (selectedRow.WorkViewItemId && selectedRow.EntryTypeName === 'Web Prepared') {
      setLoadingNoPaymentData(true)
      api.DOES_IMAGE_ENVELOPE_HAVE_PAYMENT(
        selectedRow.ImageEnvelopeId,
        (data) => {
          setShowNoPaymentWarning(!data)
          setLoadingNoPaymentData(false)
        },
        (err) => {
          console.error(err)
          setLoadingNoPaymentData(false)
        }
      )
    }
  }, [selectedRow])

  useEffect(() => {
    if (selectedRow.WorkViewItemId) {
      setLoadingFields(true)
      api.GET_WEB_PROCESS_MODAL_FIELDS(
        selectedRow.WorkViewItemId,
        (data) => {
          setFields(data)
          setLoadingFields(false)
        },
        (err) => {
          console.error(err)
          setLoadingFields(false)
        }
      )
    }
  }, [selectedRow])

  const getDetailByType = (field) => {
    switch (
      field.FieldType // This is the WebProcessModalFieldTypeCode enum
    ) {
      case 1: // DLN
        const dln = field.Value
        return {
          label: field.Label,
          value: (
            <div
              className={dln.indexOf(',') === -1 ? 'dlnTableLink' : ''}
              tabIndex={dln.indexOf(',') === -1 ? 0 : -1}
              onClick={() => {
                return dln.indexOf(',') === -1
                  ? checkDLNValid(
                      dln,
                      (id) => {
                        dispatch(openSpecificImage(id, dln))
                      },
                      (dln) => {
                        dispatch(setErrorToast('DLN ' + dln + ' not found'))
                      }
                    )
                  : null
              }}
            >
              {dln}
            </div>
          ),
        }
      case 2: // AssignedTo
        return { label: field.Label, value: <UserChip displayName={field.Value} /> }
      case 3: // RedText
        return {
          label: field.Label,
          value: (
            <div style={{ color: 'red' }}>
              <p>{field.Value}</p>
            </div>
          ),
        }
      case 0: // String
      default:
        return { label: field.Label, value: field.Value }
    }
  }

  const itemDetails = useMemo(() => {
    return fields.map((field) => getDetailByType(field))
  }, [fields])

  const supervisor = () => {
    dispatch(
      sendToSupervisor(selectedRow.WorkViewItemId, () => {
        setWebModalOpen(false)
        refreshView()
      })
    )
  }

  return (
    <div className="wvm-modal">
      {loading || loadingFields ? (
        <div className="loader-wrapper">
          <Spinner className="page-loader" themeColor="dark-neutral" />
        </div>
      ) : (
        <React.Fragment>
          <div className="info-row">
            <DetailList
              details={itemDetails}
              topBorder
              bottomBorder
              customCss={{ width: '100%' }}
            />
          </div>

          {!loadingNoPaymentData && showNoPaymentWarning && (
            <div className="no-payment-warning">No Payment found in Image Envelope.</div>
          )}
          {loadingNoPaymentData && (
            <div style={{ justifySelf: 'center', marginTop: '5px', marginBottom: '20px' }}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}

          <ContextBar
            elevation="standard"
            customCss={{ marginBottom: '15px' }}
            className="granite-context-bar"
          >
            <div className="context-button-wrapper">
              <ContextButton
                onClick={() => {
                  navToCorrespondence(selectedRow, 1)
                }}
                variant="danger"
                className={
                  !appUserRights.includes(`FileRejection`) || !selectedRow.AllowRejections
                    ? 'hidden'
                    : ''
                }
              >
                Reject
              </ContextButton>
              <ContextButton
                onClick={() => {
                  navToCorrespondence(selectedRow, 2)
                }}
                variant="secondary"
                className={
                  !appUserRights.includes(`FileCorrection`) || !selectedRow.AllowCorrections
                    ? 'hidden'
                    : ''
                }
              >
                Correct
              </ContextButton>
              <ContextButton
                disabled={selectedRow.ProcessingStatusName === 'Supervisor Review'}
                onClick={supervisor}
                variant="secondary"
              >
                Supervisor Review
              </ContextButton>
              <ContextButton
                disabled={loadingNoPaymentData}
                onClick={approveItem}
                rightIcon={
                  <div className="checkmark-wrapper right">
                    <CheckmarkIcon />
                  </div>
                }
                variant="primary"
              >
                Approve
              </ContextButton>
            </div>
          </ContextBar>
        </React.Fragment>
      )}
      {/* TRADEMARK-AMENDMENT ONLY. cropPrompt.open is set solely by approveItem (trademark
          amendments only); the isOpened check below ALSO re-verifies trademark, so no other
          source type can ever render this modal. */}
      <Portal
        isOpened={cropPrompt.open && selectedRow.SourceTypeId === FilingSourceTypeIds.Trademark}
      >
        <ConfirmDialog
          closeModal={() => {
            const dismissed = !cropActionTakenRef.current
            const onDismiss = cropPrompt.onDismiss
            setCropPrompt({ open: false, onYes: null, onNo: null, onDismiss: null })
            if (dismissed && onDismiss) onDismiss()
          }}
          onModalClosed={() => {}}
          message={`Does this amendment need the trademark specimen cropped?`}
          buttons={[
            {
              label: 'No, finish',
              onClick: () => cropPrompt.onNo && cropPrompt.onNo(),
              className: 'btn-default',
            },
            {
              label: 'Yes, crop',
              onClick: () => cropPrompt.onYes && cropPrompt.onYes(),
              className: 'btn-primary',
            },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
    </div>
  )
}

export default WebProcessModal
